/**
 * Skogstad Sport – produktside (redesign)
 * ------------------------------------------------------------------
 * Små, avgrensede oppgaver som ikke hører hjemme i galleriet:
 *
 *   1. «Les mer» ruller til «Om plagget» i stedet for å pakke ut en
 *      accordion i kjøpsboksen. Kjøpsboksen skal svare på kjøpet, ikke
 *      inneholde hele produkttekstene.
 *
 *   2. Utsolgt kombinasjon. Er kjøpsknappen deaktivert fordi valgt farge og
 *      størrelse ikke finnes på lager, sier vi det rett under størrelsene.
 *      En deaktivert knapp sender ingen klikk, så forklaringen kan ikke
 *      vente på at kunden trykker på den.
 *
 *   3. Ankerlenker (vurderingen → anmeldelsene) med respekt for sticky
 *      header og for prefers-reduced-motion.
 *
 *   4. Kapitler som er åpne på desktop og sammenslått på mobil
 *      (Spesifikasjoner). Attributtet `open` står i markupen og fjernes her
 *      under 769 px – ikke omvendt, slik at en mobil uten JS ser innholdet
 *      i stedet for en lukket boks den ikke kan åpne.
 *
 *   5. Varenummeret over produktnavnet følger fargevalget. Serverrendringen
 *      dekker første variant; bytter kunden farge, skriver vi det nye
 *      nummeret i samme format.
 *
 *   6. Tillitsmerkene (fornøydgaranti, Svanemerket …) har en kort
 *      forklaring i en popover. Hover og fokus er CSS; her ligger trykk
 *      (mobil), Escape og klikk utenfor, og et lite sidevalg så popoveren
 *      ikke går ut av kjøpskolonnen på merket lengst til høyre.
 *
 *   7. Kjøpspanelet er sticky bare når det får plass. Panelet skal aldri ha
 *      sitt eget rullefelt, og et sticky panel som er høyere enn skjermen
 *      gjemmer bunnen sin. Får det ikke plass, ruller kolonnen med siden.
 */
(function () {
  'use strict';

  function seksjon() {
    return document.querySelector('[data-section-type="product"]');
  }

  /**
   * Temaet setter --header-height på <html> (theme.js), og kunngjøringsbaren
   * legger --sticky-announcement-bar-height på toppen av den. Vi leser de
   * samme variablene som resten av Skogstad-CSS-en bruker, i stedet for å
   * måle DOM-en selv.
   */
  function headerHoyde() {
    var stil = window.getComputedStyle(document.documentElement);
    var h = parseInt(stil.getPropertyValue('--header-height'), 10) || 70;
    var bar = parseInt(stil.getPropertyValue('--sticky-announcement-bar-height'), 10) || 0;
    return h + bar + 16;
  }

  function rullTil(element) {
    if (!element) return;
    var y = element.getBoundingClientRect().top + window.pageYOffset - headerHoyde();
    var mykt = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: y, behavior: mykt ? 'smooth' : 'auto' });
  }

  /* ---------- 1. Utsolgt kombinasjon ---------- */
  /**
   * Når kunden velger en kombinasjon som ikke finnes på lager, deaktiverer
   * theme.js kjøpsknappen. En deaktivert knapp sender ingen klikk — heller
   * ikke til forfedre, heller ikke i fangstfasen — så en validering som
   * venter på et klikk ville aldri utløst. Vi speiler i stedet knappens
   * tilstand rett under størrelsene, der kunden kan gjøre noe med den.
   */
  function utsolgtvakt() {
    var rot = seksjon();
    if (!rot) return;

    var velger = rot.querySelector('.sk-velger--storrelse');
    var varsel = rot.querySelector('[data-sk-utsolgt-varsel]');
    var knapp = rot.querySelector('[data-add-to-cart]');
    if (!velger || !varsel || !knapp) return;
    if (!enGang(velger, 'skVakt')) return;

    function speil() {
      var utsolgt = knapp.disabled || knapp.getAttribute('aria-disabled') === 'true';
      velger.classList.toggle('sk-mangler', utsolgt);
    }

    // theme.js oppdaterer knappen etter variantbyttet, ikke før.
    document.addEventListener('variant:change', function () {
      window.requestAnimationFrame(speil);
    });
    speil();
  }

  /* ---------- 2. Ankerlenker ---------- */
  function ankere() {
    document.querySelectorAll('.sk-pdp a[href^="#"], .sk-seksjon a[href^="#"]').forEach(function (lenke) {
      if (!enGang(lenke, 'skBundet')) return;
      lenke.addEventListener('click', function (evt) {
        var id = lenke.getAttribute('href');
        if (!id || id === '#') return;
        var maal = document.querySelector(id);
        if (!maal) return;
        evt.preventDefault();
        rullTil(maal);
        maal.setAttribute('tabindex', '-1');
        maal.focus({ preventScroll: true });
        if (history.replaceState) history.replaceState(null, '', id);
      });
    });
  }

  /**
   * Idempotens. shopify:section:load fyrer for HVER seksjon som rendres på
   * nytt i theme editor, også seksjoner denne fila ikke eier. Uten en vakt
   * per element ville ankerlenkene fått en ny klikklytter for hver reload,
   * og et klikk ville rullet flere ganger.
   */
  function enGang(element, merke) {
    if (element.dataset[merke] === 'true') return false;
    element.dataset[merke] = 'true';
    return true;
  }

  /**
   * Sammenslåtte kapitler.
   *
   * Terskelen er 768 px, samme sted som alt annet i stilarkene bryter, og
   * den leses fra matchMedia og ikke fra innerWidth: da holder JS-en og CSS-en
   * seg synkronisert av seg selv, og vi slipper en resize-lytter som fyrer
   * hundre ganger mens brukeren drar i vindusrammen.
   *
   * Tilstanden settes på nytt hver gang terskelen krysses. Det betyr at et
   * kapittel kunden har åpnet på mobil lukkes igjen om hun roterer telefonen
   * til landskap og over terskelen — men alternativet er et kapittel som blir
   * hengende lukket på desktop, der pila er skjult og summary ikke tar imot
   * klikk. Da er det ingen vei ut.
   */
  function foldMobil() {
    var felt = document.querySelectorAll('[data-sk-fold-mobil]');
    if (!felt.length) return;

    var mq = window.matchMedia('(max-width: 768px)');

    function sett() {
      document.querySelectorAll('[data-sk-fold-mobil]').forEach(function (el) {
        if (mq.matches) {
          el.removeAttribute('open');
        } else {
          el.setAttribute('open', '');
        }
      });
    }

    sett();

    // Lytteren hører på mediespørringen og ikke på et element, så vakten må
    // ligge på noe som overlever shopify:section:load. documentElement gjør det.
    if (enGang(document.documentElement, 'skFoldLyttes')) {
      if (mq.addEventListener) {
        mq.addEventListener('change', sett);
      } else if (mq.addListener) {
        mq.addListener(sett);
      }
    }
  }

  /* ---------- 5. Varenummer over produktnavnet ---------- */
  /**
   * Trimit-SKU-en er 15 tegn: 6 sifre stil + 3 sifre farge + 6 sifre
   * størrelse (254074 772 000011). Kunden skal se de ni første som
   * «254074772»; størrelseslinja er lagerdata og hører ikke hjemme på
   * siden. Samme kutt som galleriets fargenøkkel gjør i skogstad-galleri.js.
   */
  function formaterSku(sku) {
    var rein = sku ? String(sku).trim() : '';
    if (rein.length < 9) return rein;
    return rein.slice(0, 9);
  }

  /**
   * Fargebytte gir ny variant og dermed nytt fargesiffer. Liquid har rendret
   * første variant; herfra eier vi linja. Elementet ligger i DOM-en også når
   * varianten mangler SKU (hidden), slik at et senere fargevalg med nummer
   * har en node å skrive i.
   */
  function varenummer() {
    var rot = seksjon();
    if (!rot) return;

    var linje = rot.querySelector('[data-sk-sku]');
    if (!linje) return;
    if (!enGang(linje, 'skSkuBundet')) return;

    /* «SKU»-etiketten står som ren tekst i <p>-en, tallet i eget span.
       Skriver vi i <p>-en, forsvinner etiketten ved første fargebytte —
       derfor eier vi bare verdinoden. Fallbacket til <p>-en holder
       funksjonen i live skulle markupen en dag miste spannet. */
    var verdi = linje.querySelector('[data-sk-sku-verdi]') || linje;

    document.addEventListener('variant:change', function (evt) {
      if (!linje.isConnected) return;
      var variant = evt.detail && evt.detail.variant;
      var tekst = variant ? formaterSku(variant.sku) : '';
      verdi.textContent = tekst;
      linje.hidden = tekst === '';
    });
  }

  /* ---------- 6. Tillitsmerker: popover ---------- */
  function merker() {
    var rad = seksjon() && seksjon().querySelector('[data-skogstad-merker]');
    if (!rad || rad.dataset.skMerkerKlar) return;
    rad.dataset.skMerkerKlar = '1';

    var knapper = rad.querySelectorAll('.skogstad-merke__knapp[aria-expanded]');

    function lukkAlle(unntatt) {
      knapper.forEach(function (k) {
        if (k === unntatt) return;
        k.setAttribute('aria-expanded', 'false');
        k.parentNode.classList.remove('er-apen');
      });
    }

    /* Ligger popoveren utenfor kolonnen til høyre, festes den til høyre
       kant i stedet. Målt på selve popoveren etter at den er vist. */
    function plasser(merke) {
      var pop = merke.querySelector('.skogstad-merke__pop');
      if (!pop) return;
      merke.classList.remove('skogstad-merke--hoyre');
      var grense = rad.getBoundingClientRect().right;
      if (pop.getBoundingClientRect().right > grense) {
        merke.classList.add('skogstad-merke--hoyre');
      }
    }

    knapper.forEach(function (knapp) {
      knapp.addEventListener('click', function (evt) {
        evt.preventDefault();
        var apen = knapp.getAttribute('aria-expanded') === 'true';
        lukkAlle(knapp);
        knapp.setAttribute('aria-expanded', apen ? 'false' : 'true');
        knapp.parentNode.classList.toggle('er-apen', !apen);
        if (!apen) plasser(knapp.parentNode);
      });
      /* Hover-visningen (CSS) trenger også sidevalget. */
      knapp.parentNode.addEventListener('mouseenter', function () {
        plasser(knapp.parentNode);
      });
    });

    document.addEventListener('click', function (evt) {
      if (!rad.contains(evt.target)) lukkAlle();
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') lukkAlle();
    });
  }

  /* ------------------------------------------------------------------
     «Les mer» på pitchen (21.09.2026)

     Pitchen er klippet til to linjer på desktop for å få kjøpsknappen over
     folden (se seksjon 14 i skogstad-pdp.css). Knappen pakker den ut der
     den står — den navigerer ikke til «Om plagget», og den finnes ikke på
     mobil, der pitchen uansett ligger under kjøpsknappen og er uklippet.

     Knappen står `hidden` i markupen og vises bare når teksten faktisk er
     lengre enn klippet. Kort pitch = ingen knapp: å be kunden trykke for å
     se to setninger hun allerede ser er verre enn ingen knapp i det hele
     tatt. scrollHeight sammenlignes med clientHeight på det klippede
     avsnittet; er de like, er det ingenting skjult.
     ------------------------------------------------------------------ */
  function pitchLesMer() {
    var blokk = document.querySelector('.sk-blokk--pitch');
    if (!blokk) { return; }

    var tekst = blokk.querySelector('[data-pitch]');
    var knapp = blokk.querySelector('[data-pitch-toggle]');
    if (!tekst || !knapp) { return; }

    function vurder() {
      /* Er blokka utvidet, er clientHeight lik scrollHeight uansett — da
         sier målingen ingenting, og knappen skal uansett stå. */
      if (blokk.classList.contains('er-utvidet')) { return; }
      var klippet = tekst.scrollHeight - tekst.clientHeight > 1;
      knapp.hidden = !klippet;
    }

    knapp.addEventListener('click', function () {
      var utvidet = blokk.classList.toggle('er-utvidet');
      knapp.setAttribute('aria-expanded', utvidet ? 'true' : 'false');
      knapp.textContent = utvidet
        ? knapp.getAttribute('data-mindre')
        : knapp.getAttribute('data-mer');
    });

    vurder();
    /* Bredden på kjøpskolonnen bestemmer hvor mange ord som får plass på to
       linjer, og under 769 px er klippet borte helt. Derfor måles det på
       nytt ved resize, med en ramme forsinkelse så vi leser etter layout. */
    var ventende = false;
    window.addEventListener('resize', function () {
      if (ventende) { return; }
      ventende = true;
      window.requestAnimationFrame(function () {
        ventende = false;
        vurder();
      });
    });
  }

  /* ---------- 7. Kjøpspanelet: sticky bare når det får plass ----------
   *
   * Panelet hadde `max-height` + `overflow-y: auto` som sikring mot å bli
   * høyere enn skjermen. Sikringen kostet et eget, usynlig rullefelt i
   * høyrekolonnen — kunden kunne rulle i panelet uten å rulle på siden, og
   * lagerstatus, frakt og retur sto under kanten til hun fant det.
   *
   * Sikringen er borte, og CSS-en i seksjon 14 er strammet inn så panelet
   * får plass med god margin (~691 px mot 772 tilgjengelige ved 1440 × 812).
   * Men budsjettet kan sprekke: et uvanlig langt produktnavn, en ny blokk i
   * temaeditoren, en lavere skjerm. Et sticky panel som er høyere enn
   * skjermen låser seg fast med bunnen utenfor viewporten, og da finnes det
   * ingen måte å se de nederste linjene — samme feil som rullefeltet løste,
   * bare uten rullefelt.
   *
   * Derfor: måler panelet mot plassen det har, og slår av sticky når det
   * ikke holder. Da ruller kolonnen med siden, alt er lesbart, og ingenting
   * er skjult. Vi legger aldri rullefeltet tilbake.
   *
   * `top` leses av computed style i stedet for å regnes ut her, slik at
   * CSS-en blir én sannhet også når kunngjøringsbaren er på.
   */
  function stickyVakt() {
    var panel = document.querySelector('.sk-pdp__kjop');
    if (!panel) return;
    if (!enGang(panel, 'skStickyVakt')) return;

    var KLASSE = 'sk-pdp__kjop--statisk';

    function vurder() {
      /* Under 769 px er panelet statisk fra CSS-en uansett. Klassen fjernes
         likevel, så et bytte fra mobil til desktop i devtools ikke sitter
         igjen med en tilstand som ble målt på en annen bredde. */
      if (window.matchMedia('(max-width: 768px)').matches) {
        panel.classList.remove(KLASSE);
        return;
      }

      /* Høyden må måles UTEN klassen: med den satt er panelet statisk, og
         `top` er da `auto`. Vi tar den av, måler, og setter den tilbake bare
         hvis målingen sier at den trengs. */
      panel.classList.remove(KLASSE);
      var topp = parseFloat(window.getComputedStyle(panel).top) || 0;
      /* 16 px pusterom mot skjermkanten: et panel som slutter helt nede i
         kanten ser ut som om det er klippet. */
      var plass = window.innerHeight - topp - 16;
      if (panel.offsetHeight > plass) panel.classList.add(KLASSE);
    }

    vurder();

    var ventende = false;
    window.addEventListener('resize', function () {
      if (ventende) return;
      ventende = true;
      window.requestAnimationFrame(function () {
        ventende = false;
        vurder();
      });
    });

    /* Fargebytte kan endre høyden: et annet fargenavn brytes ulikt, og en
       variant uten lager legger «Ikke på lager»-varselet inn i panelet. */
    document.addEventListener('variant:change', function () {
      window.requestAnimationFrame(vurder);
    });

    /* «Les mer» pakker ut pitchen og legger fire linjer til panelet. Da er
       det riktig at sticky gir seg — teksten kunden nettopp åpnet skal være
       lesbar i sin helhet. */
    var mer = panel.querySelector('[data-pitch-toggle]');
    if (mer) {
      mer.addEventListener('click', function () {
        window.requestAnimationFrame(vurder);
      });
    }
  }

  function start() {
    utsolgtvakt();
    ankere();
    foldMobil();
    varenummer();
    merker();
    pitchLesMer();
    stickyVakt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
})();
